import React, { useEffect, useState, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Plane, Car, Compass, Building2, ExternalLink, ArrowLeft, Phone, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";

const ICON = { flight: Plane, car: Car, activity: Compass, hotel: Building2 };
const PROVIDER_LABEL = {
  google_flights: "Janté Preferred Flights",
  preferred_flights: "Janté Preferred Flights",
  thrifty: "Janté Preferred Cars",
  enterprise: "Janté Preferred Cars",
  preferred_cars: "Janté Preferred Cars",
  preferred_cars_e: "Janté Preferred Cars",
  viator: "Janté Curated Experiences",
  preferred_activities: "Janté Curated Experiences",
  host: "Janté Host",
};

export default function ItineraryDetail() {
  const { id } = useParams();
  const [itin, setItin] = useState(null);
  const [busy, setBusy] = useState(true);
  const [adding, setAdding] = useState(false);
  const [newItem, setNewItem] = useState({ type: "flight", title: "", provider: "preferred_flights" });
  const navigate = useNavigate();

  const load = useCallback(async () => {
    try {
      const r = await api.get(`/itineraries/${id}`);
      setItin(r.data);
    } catch (e) {
      toast.error("Itinerary not found.");
      navigate("/portal");
    } finally {
      setBusy(false);
    }
  }, [id, navigate]);

  useEffect(() => {
    load();
  }, [load]);

  const updateItin = async (patch) => {
    const next = { ...itin, ...patch };
    try {
      const r = await api.put(`/itineraries/${id}`, {
        title: next.title,
        destination: next.destination,
        start_date: next.start_date,
        end_date: next.end_date,
        travelers: next.travelers,
        notes: next.notes,
        items: next.items,
        cover_image: next.cover_image,
      });
      setItin(r.data);
      toast.success("Updated.");
    } catch (e) {
      toast.error("Update failed.");
    }
  };

  const addItem = async () => {
    if (!newItem.title.trim()) return;
    const items = [...(itin.items || []), { ...newItem }];
    setAdding(false);
    setNewItem({ type: "flight", title: "", provider: "preferred_flights" });
    await updateItin({ items });
  };

  const removeItem = async (idx) => {
    const items = (itin.items || []).filter((_, i) => i !== idx);
    await updateItin({ items });
  };

  if (busy || !itin) return <div className="max-w-7xl mx-auto px-6 py-16 text-[var(--muted)]">Loading…</div>;

  return (
    <div
      className="max-w-6xl mx-auto px-6 lg:px-12 py-12 no-copy"
      data-testid="itinerary-detail"
      data-protected="true"
      onCopy={(e) => {
        // Allow copying from form inputs the host is editing.
        const tag = (e.target?.tagName || "").toLowerCase();
        if (tag === "input" || tag === "textarea") return;
        e.preventDefault();
        toast.message("Copying is disabled while booking APIs are being finalised.");
      }}
      onContextMenu={(e) => {
        const tag = (e.target?.tagName || "").toLowerCase();
        if (tag === "input" || tag === "textarea") return;
        e.preventDefault();
      }}
    >
      <button onClick={() => navigate("/portal")} className="btn-ghost mb-6" data-testid="back-to-portal">
        <ArrowLeft size={14} strokeWidth={1.25} /> Portal
      </button>

      <div className="grid md:grid-cols-3 gap-10 items-start">
        <div className="md:col-span-2">
          <div className="overline">{itin.destination}</div>
          <h1 className="font-display text-5xl text-[var(--emerald)] mt-3">{itin.title}</h1>
          <div className="text-[var(--ink-2)] mt-3">
            {itin.start_date} → {itin.end_date} · {itin.travelers} traveler{itin.travelers > 1 ? "s" : ""}
          </div>
          {itin.notes && (
            <div className="lux-card mt-6 whitespace-pre-wrap text-[var(--ink-2)] text-sm leading-relaxed">
              {itin.notes}
            </div>
          )}
        </div>

        <div className="lux-card-dark">
          <div className="overline text-[var(--gold)]">A live touch</div>
          <div className="font-display text-2xl mt-2">Concierge handoff</div>
          <p className="text-sm opacity-90 mt-2">
            Need this dialed in by a human? Our concierge takes it from here.
          </p>
          <button onClick={() => navigate("/concierge")} className="btn-gold w-full mt-4" data-testid="itin-concierge-btn">
            <Phone size={14} strokeWidth={1.25} /> Transfer to concierge
          </button>
        </div>
      </div>

      <div className="mt-14">
        <div className="flex items-center justify-between mb-6">
          <div className="overline">Itinerary items</div>
          <button onClick={() => setAdding(true)} className="btn-ghost" data-testid="add-item-btn">
            <Plus size={14} strokeWidth={1.5} /> Add item
          </button>
        </div>

        {adding && (
          <div className="lux-card mb-6 grid sm:grid-cols-4 gap-4 items-end" data-testid="add-item-panel">
            <div>
              <div className="overline mb-1">Type</div>
              <select
                value={newItem.type}
                onChange={(e) => {
                  const t = e.target.value;
                  const provDefault = { flight: "preferred_flights", car: "preferred_cars", activity: "preferred_activities", hotel: "host" }[t];
                  setNewItem({ ...newItem, type: t, provider: provDefault });
                }}
                className="lux-input"
              >
                <option value="flight">Flight</option>
                <option value="car">Car</option>
                <option value="activity">Activity</option>
                <option value="hotel">Hotel</option>
              </select>
            </div>
            <div>
              <div className="overline mb-1">Provider</div>
              <select
                value={newItem.provider}
                onChange={(e) => setNewItem({ ...newItem, provider: e.target.value })}
                className="lux-input"
              >
                {newItem.type === "flight" && <option value="preferred_flights">Janté Preferred Flights</option>}
                {newItem.type === "car" && (
                  <>
                    <option value="preferred_cars">Janté Preferred Cars (A)</option>
                    <option value="preferred_cars_e">Janté Preferred Cars (B)</option>
                  </>
                )}
                {newItem.type === "activity" && <option value="preferred_activities">Janté Curated Experiences</option>}
                {newItem.type === "hotel" && <option value="host">Janté Host</option>}
              </select>
            </div>
            <div className="sm:col-span-2">
              <div className="overline mb-1">Title</div>
              <input
                className="lux-input"
                value={newItem.title}
                onChange={(e) => setNewItem({ ...newItem, title: e.target.value })}
                placeholder="e.g. NYC → Rome non-stop"
                data-testid="add-item-title"
              />
            </div>
            <div className="sm:col-span-4 flex gap-3">
              <button onClick={addItem} className="btn-primary" data-testid="add-item-confirm">
                Add
              </button>
              <button onClick={() => setAdding(false)} className="btn-ghost">
                Cancel
              </button>
            </div>
          </div>
        )}

        <div className="space-y-4">
          {(itin.items || []).map((it, idx) => {
            const Icon = ICON[it.type] || Compass;
            return (
              <div key={it.id || `${it.type || "itm"}-${it.title || ""}-${idx}`} className="lux-card flex items-start gap-5" data-testid={`itin-item-${idx}`}>
                <div className="w-12 h-12 border border-[var(--gold)]/40 flex items-center justify-center text-[var(--emerald)] shrink-0">
                  <Icon size={20} strokeWidth={1.25} />
                </div>
                <div className="flex-1">
                  <div className="overline">{PROVIDER_LABEL[it.provider] || it.provider || it.type}</div>
                  <div className="font-display text-2xl text-[var(--emerald)] mt-1">{it.title}</div>
                  {it.description && <p className="text-sm text-[var(--ink-2)] mt-2">{it.description}</p>}
                  <div className="flex items-center gap-4 mt-4">
                    {it.booking_url && (
                      <a
                        href={it.booking_url}
                        target="_blank"
                        rel="noreferrer"
                        className="btn-gold text-xs"
                        data-testid={`book-item-${idx}`}
                      >
                        Book with Janté <ExternalLink size={12} strokeWidth={1.5} />
                      </a>
                    )}
                  </div>
                </div>
                <button
                  onClick={() => removeItem(idx)}
                  className="btn-ghost text-[var(--burgundy)]"
                  aria-label="Remove item"
                  data-testid={`remove-item-${idx}`}
                >
                  <Trash2 size={14} strokeWidth={1.25} />
                </button>
              </div>
            );
          })}
          {(itin.items || []).length === 0 && (
            <div className="text-[var(--muted)] text-sm">No items yet — add a flight, car, or activity.</div>
          )}
        </div>
      </div>
    </div>
  );
}
